import { useMutation } from '@apollo/client';
import { ACCOUNT_GET_BUYER_DRAFT_ANIMALS } from '@application/api/account/applications/useGetBuyerDraftAnimals';
import { graphql } from '@gql/gql';
import type {
  MtAnimalBuyerGetAnimalQuery,
  MtAnimalBuyerUpdateAnimalMutation,
  MtAnimalBuyerUpdateAnimalMutationVariables,
} from '@gql/graphql';
import type { BuyerAnimalFormValues } from '@presentation/components';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';
import { useAnimalUpdateBuilder } from './useAnimalUpdateBuilder';

export const MT_ANIMAL_BUYER_UPDATE_ANIMAL = graphql(`
  mutation MtAnimalBuyerUpdateAnimal(
    $data: AnimalUpdateInput!
    $animalId: Int!
    $imageUpload: Upload
  ) {
    updateAnimal(data: $data, animalId: $animalId, imageUpload: $imageUpload) {
      ...BuyerAnimalInfo
    }
  }
`);

export const useUpdateAnimal = () => {
  const [updateAnimal, { error, loading }] = useMutation<
    MtAnimalBuyerUpdateAnimalMutation,
    MtAnimalBuyerUpdateAnimalMutationVariables
  >(MT_ANIMAL_BUYER_UPDATE_ANIMAL, {
    refetchQueries: [ACCOUNT_GET_BUYER_DRAFT_ANIMALS],
  });

  const { animalUpdateBuilder } = useAnimalUpdateBuilder();

  const updateAnimalFromForm = useCallback(
    (
      form: BuyerAnimalFormValues,
      animal: NonNullable<MtAnimalBuyerGetAnimalQuery['accountMe']>['animals'][number] | undefined,
    ) => {
      if (!animal) {
        throw new Error('Animal is undefined');
      }
      const { data } = animalUpdateBuilder(form, animal);

      const imagesUpload: Scalar_Upload[] = cleanDeep(form.images ?? [])
        ?.map((file) => (typeof file === 'string' ? undefined : (file as Scalar_Upload)))
        .filter((file) => file != null) as Scalar_Upload[];

      return updateAnimal({
        variables: {
          data,
          animalId: animal.id,
          ...(imagesUpload?.[0] && { imageUpload: imagesUpload[0] }),
        },
      });
    },
    [animalUpdateBuilder, updateAnimal],
  );

  const updateAnimalArchive = useCallback(
    async (animalId?: number) => {
      if (!animalId) return;
      return updateAnimal({
        variables: {
          animalId,
          data: {
            archived: { set: true },
          },
        },
      });
    },
    [updateAnimal],
  );

  const updateAnimalUnarchive = useCallback(
    async (animalId?: number) => {
      if (!animalId) return;
      return updateAnimal({
        variables: {
          animalId,
          data: {
            archived: { set: false },
          },
        },
      });
    },
    [updateAnimal],
  );

  return {
    updateAnimalLoading: loading,
    updateAnimalError: error,
    updateAnimalFromForm,
    updateAnimal,
    updateAnimalArchive,
    updateAnimalUnarchive,
  };
};
